import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, Plus, X, Upload, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const CreateSorteo = ({ adminData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    sorteo_type: 'standard',
    prizes: [''],
    prize_images: [],
    logo: null,
    whatsapp_number: '',
    observations: '',
    total_numbers: ''
  });

  // Verificar si puede crear sorteos
  const canCreateSorteos = () => {
    if (!adminData) return true; // Por compatibilidad
    if (adminData.is_owner) return true;
    if (!adminData.enabled) return false;
    if (adminData.subscription_status === 'expired') return false;
    if (adminData.subscription_status === 'none') return false;
    return true;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrizeChange = (index, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = value;
    setFormData(prev => ({ ...prev, prizes: newPrizes }));
  };

  const addPrize = () => {
    setFormData(prev => ({ ...prev, prizes: [...prev.prizes, ''] }));
  };

  const removePrize = (index) => {
    if (formData.prizes.length > 1) {
      const newPrizes = formData.prizes.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, prizes: newPrizes }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('El logo no puede superar 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, logo: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrizeImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = formData.prize_images.length + files.length;
    
    if (totalImages > 5) {
      toast.error('Máximo 5 imágenes de premios');
      return;
    }

    files.forEach(file => {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} supera 2MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          prize_images: [...prev.prize_images, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePrizeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      prize_images: prev.prize_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.title.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    
    if (!formData.prizes.some(p => p.trim())) {
      toast.error('Agregá al menos un premio');
      return;
    }
    
    if (!formData.whatsapp_number.trim()) {
      toast.error('El número de WhatsApp es obligatorio');
      return;
    }

    if (formData.sorteo_type === 'custom' && (!formData.total_numbers || parseInt(formData.total_numbers) <= 0)) {
      toast.error('Indicá la cantidad de números para el sorteo personalizado');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        sorteo_type: formData.sorteo_type,
        prizes: formData.prizes.filter(p => p.trim()),
        prize_images: formData.prize_images,
        logo: formData.logo,
        whatsapp_number: formData.whatsapp_number,
        observations: formData.observations || undefined,
        total_numbers: formData.sorteo_type === 'custom' ? parseInt(formData.total_numbers) : null
      };

      await axios.post(`${API}/sorteos`, payload);
      toast.success('¡Sorteo creado exitosamente!');
      navigate('/admin/sorteos');
    } catch (error) {
      console.error('Error creating sorteo:', error);
      toast.error(error.response?.data?.detail || 'Error al crear el sorteo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Button
        variant="outline"
        onClick={() => navigate('/admin/sorteos')}
      >
        <ArrowLeft className="mr-2" size={20} />
        Volver
      </Button>

      {/* Mensaje de suscripción vencida */}
      {!canCreateSorteos() && (
        <Card className="bg-red-50 border-red-300">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="text-red-600" size={40} />
              <div>
                <h3 className="text-xl font-bold text-red-800">No podés crear nuevos sorteos</h3>
                <p className="text-red-600 mt-1">
                  {!adminData?.enabled 
                    ? 'Tu cuenta está deshabilitada. Contactá al administrador.'
                    : 'Tu suscripción ha vencido. Contactá al administrador para renovar y seguir creando sorteos.'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {canCreateSorteos() && (
      <Card>
        <CardHeader>
          <CardTitle className="text-3xl">Crear Nuevo Sorteo</CardTitle>
          <CardDescription>
            Los sorteos son gratuitos y permiten 1 número por persona
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Información Básica */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Información Básica</h3>
              
              <div>
                <Label htmlFor="title">Título del Sorteo *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Ej: Gran Sorteo de Navidad"
                  required
                />
              </div>

              <div>
                <Label htmlFor="sorteo_type">Tipo de Sorteo *</Label>
                <Select
                  value={formData.sorteo_type}
                  onValueChange={(value) => handleInputChange('sorteo_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grid_100">00-99 (100 numeros)</SelectItem>
                    <SelectItem value="standard">000-999 (1000 numeros)</SelectItem>
                    <SelectItem value="grid_10000">0000-9999 (10000 numeros)</SelectItem>
                    <SelectItem value="lines_20">20 Líneas x 5 (00-99)</SelectItem>
                    <SelectItem value="lines_50">Lineas 50 (50 filas x 2 numeros, del 00 al 99)</SelectItem>
                    <SelectItem value="lines_500">500 Líneas x 2 (del 000 al 999)</SelectItem>
                    <SelectItem value="lines">Lineas 200 (200 filas x 5 numeros)</SelectItem>
                    <SelectItem value="lines_125">Lineas 125 (125 filas x 8 numeros)</SelectItem>
                    <SelectItem value="lines_250">250 filas Manu (250 filas x 4 numeros)</SelectItem>
                    <SelectItem value="lines_250_osvaldo">250 Líneas Osvaldo (250 filas x 4 numeros)</SelectItem>
                    <SelectItem value="custom">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.sorteo_type === 'custom' && (
                <div>
                  <Label htmlFor="total_numbers">Cantidad de Números *</Label>
                  <Input
                    id="total_numbers"
                    type="number"
                    min="1"
                    value={formData.total_numbers}
                    onChange={(e) => handleInputChange('total_numbers', e.target.value)}
                    placeholder="Ej: 500"
                    required
                  />
                </div>
              )}

              <div>
                <Label htmlFor="whatsapp_number">Número de WhatsApp *</Label>
                <Input
                  id="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                  placeholder="Ej: +54 9 11 1234-5678"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Número al que llegarán los mensajes de los participantes
                </p>
              </div>

              <div>
                <Label htmlFor="observations">Observaciones</Label>
                <textarea
                  id="observations"
                  value={formData.observations}
                  onChange={(e) => handleInputChange('observations', e.target.value)}
                  placeholder="Aclaraciones para los participantes (fecha del sorteo, condiciones, etc.)"
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="observations-input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Aparecerá en el detalle público del sorteo (opcional).
                </p>
              </div>
            </div>

            {/* Premios */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Premios</h3>
              
              {formData.prizes.map((prize, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={prize}
                    onChange={(e) => handlePrizeChange(index, e.target.value)}
                    placeholder={`Premio ${index + 1}`}
                  />
                  {formData.prizes.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removePrize(index)}
                    >
                      <X size={20} />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button type="button" variant="outline" onClick={addPrize}>
                <Plus size={20} className="mr-2" />
                Agregar Premio
              </Button>
            </div>

            {/* Imágenes */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Imágenes</h3>
              
              <div>
                <Label>Logo del Sorteo</Label>
                <div className="mt-2">
                  {formData.logo ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.logo}
                        alt="Logo"
                        className="w-32 h-32 object-contain border rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 w-6 h-6"
                        onClick={() => setFormData(prev => ({ ...prev, logo: null }))}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center hover:border-gray-400">
                        <Upload className="text-gray-400" size={32} />
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <Label>Fotos de Premios (máx. 5)</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.prize_images.map((img, index) => (
                    <div key={index} className="relative">
                      <img
                        src={img}
                        alt={`Premio ${index + 1}`}
                        className="w-24 h-24 object-cover border rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 w-6 h-6"
                        onClick={() => removePrizeImage(index)}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                  {formData.prize_images.length < 5 && (
                    <label className="cursor-pointer">
                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded flex items-center justify-center hover:border-gray-400">
                        <Plus className="text-gray-400" size={24} />
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePrizeImageUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">ℹ️ Información del Sorteo</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Los sorteos son <strong>gratuitos</strong></li>
                <li>• Cada persona puede elegir <strong>solo 1 número</strong></li>
                <li>• El mensaje de WhatsApp NO incluye alias de pago</li>
              </ul>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/sorteos')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={20} />
                    Creando...
                  </>
                ) : (
                  'Crear Sorteo'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default CreateSorteo;
